import React, { useEffect, useState } from "react";
import { Box, ButtonGroup, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Select from "components/Select";
import ClientSelect from "components/ClientSelect/ClientSelect";
import ParkSelect from "./ParkSelect";
import { fetchProductionForecastGroup } from "store/appSlice";
import { useAppDispatch } from "hooks";

interface ForecastFilterProps {}

const ForecastFilters: React.FC<ForecastFilterProps> = ({
  groupId,
  filters,
  setFilters,
  parks,
}) => {
  const dispatch = useAppDispatch();
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(true);

  const [selectedClients, setSelectedClients] = useState<
    { title: string; value: number }[]
  >([]);
  const [selectedParks, setSelectedParks] = useState<
    { title: string; value: number }[]
  >(
    filters.productionParkIds.length > 0 && parks.length > 0
      ? filters.productionParkIds.map((id: number) => {
          return parks.find((park) => park.value === id);
        })
      : [],
  );

  const handleChangeFilters = (label, value) => {
    const filtersCopy = { ...filters };

    filtersCopy[label] = value;

    setFilters(filtersCopy);

    if (value && value.length > 0) {
      setFiltersChanged(true);
      setFiltersApplied(true);
    }
  };

  useEffect(() => {
    const clientIds = selectedClients.map((item) => item.value);

    handleChangeFilters("clientIds", clientIds);

    if (selectedClients && selectedClients.length > 0) {
      setFiltersChanged(true);
      setFiltersApplied(true);
    }
  }, [selectedClients]);

  useEffect(() => {
    const parkIds = selectedParks.map((item) => item.value);

    handleChangeFilters("productionParkIds", parkIds);

    if (selectedParks && selectedParks.length > 0) {
      setFiltersChanged(true);
      setFiltersApplied(true);
    }
  }, [selectedParks]);

  const handleSubmitFilter = () => {
    if (groupId) {
      dispatch(fetchProductionForecastGroup(groupId, filters));
      setFiltersApplied(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      productionParkType: "",
      clientIds: [],
      productionParkIds: [],
      priceArea: "",
    });
    setSelectedClients([]);
    setSelectedParks([]);
    setFiltersChanged(false);
    setFiltersApplied(false);
    if (groupId) {
      dispatch(
        fetchProductionForecastGroup(groupId, {
          productionParkType: "",
          clientIds: [],
          productionParkIds: [],
          priceArea: "",
        }),
      );
    }
  };

  const priceAreas = ["EE", "LT"];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-end",
        mb: 2,
      }}
    >
      <Box sx={{ mr: 2 }}>
        <Typography
          component="h5"
          fontWeight={500}
          sx={{ mr: "1rem", mb: "0.6rem" }}
        >
          Price area
        </Typography>
        <ButtonGroup>
          {priceAreas.map((priceArea) => (
            <Button
              key={"PriceArea-" + priceArea}
              variant={
                filters.priceArea === priceArea ? "contained" : "outlined"
              }
              sx={{ padding: "0.4rem 2rem" }}
              onClick={() => handleChangeFilters("priceArea", priceArea)}
            >
              {priceArea}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <Box sx={{ mr: 2 }}>
        <Select
          label="Park type"
          id="productionParkType"
          name="productionParkType"
          value={filters.productionParkType}
          onChange={(event) => {
            handleChangeFilters("productionParkType", event.target.value);
          }}
          minWidth={200}
          options={[
            {
              label: "All",
              value: "ALL",
            },
            {
              label: "Solar",
              value: "SOLAR",
            },
            {
              label: "Wind",
              value: "WIND",
            },
          ]}
        />
      </Box>
      <Box sx={{ mr: 1 }}>
        <ClientSelect
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          label="Clients"
          minWidth={350}
          maxWidth={350}
        />
      </Box>
      <Box sx={{ mr: 2 }}>
        <ParkSelect
          selectedParks={selectedParks}
          setSelectedParks={setSelectedParks}
          parks={parks}
          label="Parks"
          minWidth={350}
          maxWidth={350}
        />
      </Box>
      <Button
        variant="contained"
        onClick={handleSubmitFilter}
        disabled={!filtersApplied}
      >
        Filter
      </Button>
      {filtersChanged && (
        <Button variant="outlined" onClick={handleResetFilters} sx={{ ml: 1 }}>
          Clear filters
        </Button>
      )}
    </Box>
  );
};

export default ForecastFilters;
